// utils/userCartWishlistUtils.js
import axios from "axios";

const API_BASE_URL = "https://api.dremerz.net/api";
const USER_CART_KEY = "userCart";
const USER_WISHLIST_KEY = "userWishlist";

// Helper function to get current user data
const getCurrentUser = () => {
  return JSON.parse(sessionStorage.getItem("user"));
};

// Helper function to update user data in API
const updateUserInAPI = async (updateData) => {
  const user = getCurrentUser();
  if (!user) return false;

  try {
    const response = await axios.put(
      `${API_BASE_URL}/Kalanjiyam_Users_Registration/${user.id}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user in API:", error);
    return false;
  }
};

// Cart Functions
export const cartUtils = {
  getCart: () => {
    const cartData = sessionStorage.getItem(USER_CART_KEY);
    return cartData ? JSON.parse(cartData) : {};
  },

  addToCart: async (productId, quantity = 1, size = "") => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentCart = cartUtils.getCart();
    
    // Check if product with same size already exists
    const existingKey = Object.keys(currentCart).find(key => {
      const item = currentCart[key];
      return item.productId === productId && item.size === size;
    });

    // 2. Optimistically update local storage
    let newCart = {...currentCart};
    
    if (existingKey) {
      // Update quantity if same product with same size exists
      newCart[existingKey] = {
        ...newCart[existingKey],
        quantity: newCart[existingKey].quantity + quantity
      };
    } else {
      // Add new item with unique key (productId + size + timestamp)
      const newKey = `${productId}_${size}_${Date.now()}`;
      newCart[newKey] = {
        productId,
        quantity,
        size
      };
    }
    
    sessionStorage.setItem(USER_CART_KEY, JSON.stringify(newCart));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_CART_KEY, JSON.stringify(currentCart));
      alert("Failed to update cart. Please try again.");
      return false;
    }
  },

  removeFromCart: async (cartItemKey) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentCart = cartUtils.getCart();
    
    // 2. Optimistically update local storage
    const { [cartItemKey]: _, ...newCart } = currentCart;
    sessionStorage.setItem(USER_CART_KEY, JSON.stringify(newCart));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_CART_KEY, JSON.stringify(currentCart));
      alert("Failed to update cart. Please try again.");
      return false;
    }
  },

  updateQuantity: async (cartItemKey, newQuantity) => {
    if (newQuantity < 1) {
      return await cartUtils.removeFromCart(cartItemKey);
    }

    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentCart = cartUtils.getCart();
    
    // 2. Optimistically update local storage
    const newCart = {
      ...currentCart,
      [cartItemKey]: {
        ...currentCart[cartItemKey],
        quantity: newQuantity
      }
    };
    sessionStorage.setItem(USER_CART_KEY, JSON.stringify(newCart));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_CART_KEY, JSON.stringify(currentCart));
      alert("Failed to update cart. Please try again.");
      return false;
    }
  },

  clearCart: async () => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentCart = cartUtils.getCart();
    
    // 2. Optimistically clear local storage
    sessionStorage.setItem(USER_CART_KEY, JSON.stringify({}));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        cartItems: {}
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_CART_KEY, JSON.stringify(currentCart));
      console.error("Failed to clear cart. Please try again.");
      return false;
    }
  },
  
  // Helper to get count of a specific product (regardless of size)
  getProductCount: (productId) => {
    const cart = cartUtils.getCart();
    return Object.values(cart).reduce((total, item) => {
      return item.productId === productId ? total + item.quantity : total;
    }, 0);
  },
  
  // Helper to find cart item key for a product with specific size
  findCartItemKey: (productId, size) => {
    const cart = cartUtils.getCart();
    return Object.keys(cart).find(key => {
      const item = cart[key];
      return item.productId === productId && item.size === size;
    });
  }
};

// Wishlist Functions (unchanged as requested)
export const wishlistUtils = {
  getWishlist: () => {
    const wishlistData = sessionStorage.getItem(USER_WISHLIST_KEY);
    return wishlistData ? JSON.parse(wishlistData) : [];
  },

  addToWishlist: async (productId) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentWishlist = wishlistUtils.getWishlist();
    
    // Already in wishlist
    if (currentWishlist.includes(productId)) {
      return true;
    }

    // 2. Optimistically update local storage
    const newWishlist = [...currentWishlist, productId];
    sessionStorage.setItem(USER_WISHLIST_KEY, JSON.stringify(newWishlist));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        wishListItems: newWishlist
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_WISHLIST_KEY, JSON.stringify(currentWishlist));
      alert("Failed to update wishlist. Please try again.");
      return false;
    }
  },

  removeFromWishlist: async (productId) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    // 1. Get current state
    const currentWishlist = wishlistUtils.getWishlist();
    
    // 2. Optimistically update local storage
    const newWishlist = currentWishlist.filter(id => id !== productId);
    sessionStorage.setItem(USER_WISHLIST_KEY, JSON.stringify(newWishlist));

    try {
      // 3. Try to update API
      const apiSuccess = await updateUserInAPI({
        wishListItems: newWishlist
      });

      if (!apiSuccess) {
        throw new Error("API update failed");
      }

      return true;
    } catch (error) {
      // 4. Revert local changes if API fails
      sessionStorage.setItem(USER_WISHLIST_KEY, JSON.stringify(currentWishlist));
      alert("Failed to update wishlist. Please try again.");
      return false;
    }
  },

  isInWishlist: (productId) => {
    return wishlistUtils.getWishlist().includes(productId);
  }
};

// Initialize user data on login
export const initializeUserData = (userData) => {
  if (userData?.cartItems) {
    sessionStorage.setItem(USER_CART_KEY, userData.cartItems);
  } else {
    sessionStorage.setItem(USER_CART_KEY, JSON.stringify({}));
  }
  if (userData?.wishListItems) {
    sessionStorage.setItem(USER_WISHLIST_KEY, userData.wishListItems);    
  } else {
    sessionStorage.setItem(USER_WISHLIST_KEY, JSON.stringify([]));
  }
};